import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

export const fetchLoveShots = createAsyncThunk(
  'loveshots/fetchLoveShots',
  async ({ page = 1, userId }, { rejectWithValue }) => {
    try {
      let url = `/loveshots?page=${page}`;
      if (userId) {
        url += `&user_id=${userId}`;
      }
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const createLoveShot = createAsyncThunk(
  'loveshots/createLoveShot',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post('/loveshots', formData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const likeLoveshot = createAsyncThunk(
  'loveshots/likeLoveshot',
  async (loveshotId, { rejectWithValue }) => {
    try {
      const response = await api.post(`/loveshots/${loveshotId}/like`);
      return { loveshotId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const unlikeLoveshot = createAsyncThunk(
  'loveshots/unlikeLoveshot',
  async (loveshotId, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/loveshots/${loveshotId}/like`);
      return { loveshotId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const saveLoveshot = createAsyncThunk(
  'loveshots/saveLoveshot',
  async (loveshotId, { rejectWithValue }) => {
    try {
      const response = await api.post(`/loveshots/${loveshotId}/save`);
      return { loveshotId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const unsaveLoveshot = createAsyncThunk(
  'loveshots/unsaveLoveshot',
  async (loveshotId, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/loveshots/${loveshotId}/save`);
      return { loveshotId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const addComment = createAsyncThunk(
  'loveshots/addComment',
  async ({ loveshotId, content }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/loveshots/${loveshotId}/comments`, { content });
      return { loveshotId, comment: response.data.data };
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const deleteLoveshot = createAsyncThunk(
  'loveshots/deleteLoveshot',
  async (loveshotId, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/loveshots/${loveshotId}`);
      return { loveshotId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const updateLoveshotPrivacy = createAsyncThunk(
  'loveshots/updateLoveshotPrivacy',
  async ({ id, is_hidden }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/loveshots/${id}/privacy`, { is_hidden });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

const initialState = {
  items: [],
  currentPage: 1,
  hasMore: true,
  loading: false,
  error: null,
};

const loveshotsSlice = createSlice({
  name: 'loveshots',
  initialState,
  reducers: {
    clearLoveShotsError: (state) => {
      state.error = null;
    },
    resetLoveShots: (state) => {
      state.items = [];
      state.currentPage = 1;
      state.hasMore = true;
    },
    addLoveshot: (state, action) => {
      state.items.unshift(action.payload);
    },
    updateLoveshot: (state, action) => {
      const index = state.items.findIndex(item => item.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = { ...state.items[index], ...action.payload };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLoveShots.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchLoveShots.fulfilled, (state, action) => {
        state.loading = false;
        let data = null;
        let hasMore = false;
        if (Array.isArray(action.payload)) {
          data = action.payload;
        } else if (action.payload?.data) {
          if (Array.isArray(action.payload.data)) {
            data = action.payload.data;
            hasMore = action.payload.next_page_url !== null;
          } else if (action.payload.data?.data) {
            data = action.payload.data.data;
            hasMore = action.payload.data.next_page_url !== null;
          }
        }
        if (data) {
          const filteredData = data.filter(item => item);
          if (action.meta.arg.page === 1) {
            state.items = filteredData;
          } else {
            state.items = [...state.items, ...filteredData];
          }
          state.currentPage = action.meta.arg.page;
          state.hasMore = hasMore;
        }
      })
      .addCase(fetchLoveShots.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createLoveShot.fulfilled, (state, action) => {
        if (action.payload) {
          state.items.unshift(action.payload);
        }
      })
      .addCase(likeLoveshot.fulfilled, (state, action) => {
        const loveshot = state.items.find(item => item.id === action.payload.loveshotId);
        if (loveshot) {
          loveshot.is_liked = true;
          loveshot.likes_count = (loveshot.likes_count || 0) + 1;
        }
      })
      .addCase(unlikeLoveshot.fulfilled, (state, action) => {
        const loveshot = state.items.find(item => item.id === action.payload.loveshotId);
        if (loveshot) {
          loveshot.is_liked = false;
          loveshot.likes_count = Math.max((loveshot.likes_count || 0) - 1, 0);
        }
      })
      .addCase(saveLoveshot.fulfilled, (state, action) => {
        const loveshot = state.items.find(item => item.id === action.payload.loveshotId);
        if (loveshot) {
          loveshot.is_saved = true;
        }
      })
      .addCase(unsaveLoveshot.fulfilled, (state, action) => {
        const loveshot = state.items.find(item => item.id === action.payload.loveshotId);
        if (loveshot) {
          loveshot.is_saved = false;
        }
      })
      .addCase(addComment.fulfilled, (state, action) => {
        const loveshot = state.items.find(item => item.id === action.payload.loveshotId);
        if (loveshot) {
          loveshot.comments_count = (loveshot.comments_count || 0) + 1;
          loveshot.comments = loveshot.comments || [];
          loveshot.comments.push(action.payload.comment);
        }
      })
      .addCase(deleteLoveshot.fulfilled, (state, action) => {
        state.items = state.items.filter(item => item.id !== action.payload.loveshotId);
      })
      .addCase(updateLoveshotPrivacy.fulfilled, (state, action) => {
        const loveshot = state.items.find(item => item.id === action.payload.id);
        if (loveshot) {
          loveshot.is_hidden = action.payload.is_hidden;
        }
      });
  },
});

export const { clearLoveShotsError, resetLoveShots, addLoveshot, updateLoveshot } = loveshotsSlice.actions;
export default loveshotsSlice.reducer;
